import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import type { DeskDetail, DeskPriority, PriorityBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog, EmptyState } from '@/components/ui/misc';
import { errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';

function slaLine(p: DeskPriority): string {
  const parts = [
    p.attendMinutes != null ? `atender ${p.attendMinutes} min` : null,
    p.solveMinutes != null ? `resolver ${p.solveMinutes} min` : null,
  ].filter(Boolean);
  return parts.length > 0 ? `SLA: ${parts.join(' · ')}` : 'Sem SLA';
}

export function DeskPrioritiesSection({ desk }: { desk: DeskDetail }) {
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
  const [dialog, setDialog] = useState<{ priority: DeskPriority | null } | null>(null);

  const priorities = [...desk.priorities].sort((a, b) => a.position - b.position);

  const reorder = useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await api<unknown>(`/desks/${desk.id}/priorities/reorder`, {
        method: 'PUT',
        body: { ids },
      });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (priorityId: number) => {
      const res = await api<void>(`/desks/${desk.id}/priorities/${priorityId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const move = (priorityId: number, dir: -1 | 1) => {
    const ids = priorities.map((p) => p.id);
    const i = ids.indexOf(priorityId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    const a = ids[i];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[i] = b;
    ids[j] = a;
    reorder.mutate(ids);
  };

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Prioridades</h2>
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
            {priorities.length}
          </span>
        </div>
        <Button size="sm" onClick={() => setDialog({ priority: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Prioridade
        </Button>
      </div>
      {priorities.length === 0 ? (
        <EmptyState className="h-24" message="Nenhuma prioridade cadastrada" />
      ) : (
        <ul className="space-y-1.5">
          {priorities.map((p, idx) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-2 rounded-md border border-divider px-3 py-2 text-sm transition-colors hover:border-border"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground">{slaLine(p)}</div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <DeskRowAction
                  label="Mover para cima"
                  disabled={idx === 0 || reorder.isPending}
                  onClick={() => move(p.id, -1)}
                >
                  <ArrowUp className="size-3.5" />
                </DeskRowAction>
                <DeskRowAction
                  label="Mover para baixo"
                  disabled={idx === priorities.length - 1 || reorder.isPending}
                  onClick={() => move(p.id, 1)}
                >
                  <ArrowDown className="size-3.5" />
                </DeskRowAction>
                <DeskRowAction label="Editar prioridade" onClick={() => setDialog({ priority: p })}>
                  <Pencil className="size-3.5" />
                </DeskRowAction>
                <DeskRowAction
                  label="Remover prioridade"
                  destructive
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Remover a prioridade "${p.name}"?`)) remove.mutate(p.id);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </DeskRowAction>
              </div>
            </li>
          ))}
        </ul>
      )}
      {(reorder.error ?? remove.error) ? (
        <p className="mt-2 text-sm text-destructive">
          {errorMessage(reorder.error ?? remove.error)}
        </p>
      ) : null}
      {dialog ? (
        <PriorityDialog
          deskId={desk.id}
          priority={dialog.priority}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}

function parsePositiveInt(raw: string): number | null {
  const n = Number(raw);
  return raw.trim() && Number.isInteger(n) && n > 0 ? n : null;
}

function PriorityDialog({
  deskId,
  priority,
  onClose,
}: {
  deskId: number;
  priority: DeskPriority | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(priority?.name ?? '');
  const [attend, setAttend] = useState(
    priority?.attendMinutes != null ? String(priority.attendMinutes) : ''
  );
  const [solve, setSolve] = useState(
    priority?.solveMinutes != null ? String(priority.solveMinutes) : ''
  );

  const save = useMutation({
    mutationFn: async (body: PriorityBody) => {
      const res = priority
        ? await api<DeskPriority>(`/desks/${deskId}/priorities/${priority.id}`, {
            method: 'PUT',
            body,
          })
        : await api<DeskPriority>(`/desks/${deskId}/priorities`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate({
      name: name.trim(),
      attendMinutes: parsePositiveInt(attend),
      solveMinutes: parsePositiveInt(solve),
    });
  };

  return (
    <Dialog open onClose={onClose} title={priority ? 'Editar prioridade' : 'Nova prioridade'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="priority-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="priority-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="priority-attend">Atendimento (min)</Label>
            <Input
              id="priority-attend"
              type="number"
              min={1}
              value={attend}
              onChange={(e) => setAttend(e.target.value)}
              placeholder="—"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="priority-solve">Solução (min)</Label>
            <Input
              id="priority-solve"
              type="number"
              min={1}
              value={solve}
              onChange={(e) => setSolve(e.target.value)}
              placeholder="—"
            />
          </div>
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
